import {
  Accessor,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  on,
  onCleanup,
  untrack,
} from 'solid-js';
import {
  isFeatureExtractionPaneEligible,
  type FeatureExtractionContextType,
} from '@mobile-mapping/providers';
import { asError, type ILogger } from '@trimble/logging';
import type { Web3DViewer } from '@technology/web3d';
import type { LineEditingPlugin } from '@technology/web3d-plugin-line-editing';
import type { FeaturesServicePlugin } from '@trimble/web3d-plugin-features-service';

import {
  createFeatureExtractionController,
  type FeatureExtractionController,
} from './feature-extraction-controller';
import type { PointCloudRoot } from './feature-extraction-types';

export interface CadEditingViewerWrapper {
  viewer: Web3DViewer;
  lineEditingPlugin: LineEditingPlugin;
  featuresServicePlugin: FeaturesServicePlugin;
}

export interface UseCadEditingPaneParams {
  featureExtraction: FeatureExtractionContextType;
  featureExtractionLogger: ILogger;
  isEditingEligible: Accessor<boolean>;
  viewModeType: Accessor<string | undefined>;
  /** Loaded-cloud CSIB only — not sticky currentLoadedCsib (no cross-cloud borrow). */
  sceneCsib: Accessor<string | undefined>;
  pointCloudRoots: Accessor<PointCloudRoot[]>;
  getViewerWrapper(): CadEditingViewerWrapper | undefined;
  fetchGeoDataFiles(csib: string): Promise<Map<string, ArrayBuffer>>;
  onCadEditingEnabled?(): void;
  onCadEditingDisabled?(): void;
}

export interface UseCadEditingPaneResult {
  cadEditingMode: Accessor<boolean>;
  cadEditingSession: Accessor<FeatureExtractionController | undefined>;
  isCadEditingEligible: Accessor<boolean>;
  toggleCadEditingMode(): void;
}

interface GeoDataCacheEntry {
  csib: string;
  geoDataFiles: Map<string, ArrayBuffer>;
}

interface GeoFetchFailure {
  csib: string;
  nonce: number;
}

export function useCadEditingPane(
  params: UseCadEditingPaneParams
): UseCadEditingPaneResult {
  const [cadEditingMode, setCadEditingMode] = createSignal(false);
  const [cadEditingSession, setCadEditingSession] = createSignal<
    FeatureExtractionController | undefined
  >(undefined);
  const [geoFetchNonce, setGeoFetchNonce] = createSignal(0);
  const [geoFetchFailure, setGeoFetchFailure] = createSignal<
    GeoFetchFailure | undefined
  >(undefined);

  const isCadEditingEligible = createMemo(
    () =>
      isFeatureExtractionPaneEligible(
        params.isEditingEligible(),
        params.viewModeType()
      ) && params.featureExtraction.featureRepository() !== undefined
  );

  const geoDataFetchKey = createMemo(() => {
    const csib = params.sceneCsib();
    if (!csib || !isCadEditingEligible()) {
      return undefined;
    }
    return { csib, nonce: geoFetchNonce() };
  });

  const disposeCadEditingSession = (): void => {
    const session = untrack(() => cadEditingSession());
    if (session) {
      session.dispose();
      setCadEditingSession(undefined);
    }
  };

  const disableCadEditingMode = (): void => {
    disposeCadEditingSession();
    setCadEditingMode(false);
    params.onCadEditingDisabled?.();
  };

  const [geoDataEntry] = createResource(
    geoDataFetchKey,
    async ({ csib, nonce }): Promise<GeoDataCacheEntry | undefined> => {
      try {
        const geoDataFiles = await params.fetchGeoDataFiles(csib);
        if (
          untrack(
            () => geoFetchNonce() === nonce && params.sceneCsib() === csib
          )
        ) {
          setGeoFetchFailure(undefined);
        }
        return { csib, geoDataFiles };
      } catch (error: unknown) {
        params.featureExtractionLogger.warn(
          'Failed to fetch geodata files for CAD editing pane session',
          { csib },
          asError(error)
        );
        const isLatestFetch = untrack(
          () => geoFetchNonce() === nonce && params.sceneCsib() === csib
        );
        if (isLatestFetch) {
          setGeoFetchFailure({ csib, nonce });
        }
        return undefined;
      }
    }
  );

  createEffect(() => {
    if (!isCadEditingEligible() && cadEditingMode()) {
      disableCadEditingMode();
    }
  });

  createEffect(() => {
    const failure = geoFetchFailure();
    if (!failure) {
      return;
    }
    if (
      !cadEditingMode() ||
      geoFetchNonce() !== failure.nonce ||
      params.sceneCsib() !== failure.csib
    ) {
      setGeoFetchFailure(undefined);
      return;
    }
    setGeoFetchFailure(undefined);
    disableCadEditingMode();
  });

  createEffect(
    on(cadEditingMode, (enabled) => {
      if (!enabled) {
        return;
      }
      const csib = params.sceneCsib();
      const entry = geoDataEntry();
      if (csib && entry?.csib !== csib) {
        setGeoFetchNonce((nonce) => nonce + 1);
      }
    })
  );

  createEffect(() => {
    const enabled = cadEditingMode();
    const eligible = isCadEditingEligible();
    const csib = params.sceneCsib();
    const cache = geoDataEntry();
    const existing = untrack(() => cadEditingSession());
    // Region change swaps featureRepository; subscribe for the dependency so the
    // session rebuilds. The session reads the current value through the context.
    void params.featureExtraction.featureRepository();

    if (!enabled || !eligible) {
      if (existing) {
        disposeCadEditingSession();
      }
      return;
    }

    const viewerWrapper = params.getViewerWrapper();
    if (!viewerWrapper || !csib) {
      if (!existing) {
        params.featureExtractionLogger.info(
          'CAD editing enabled but pane session not started',
          {
            hasViewer: Boolean(viewerWrapper),
            hasCsib: Boolean(csib),
          }
        );
      }
      return;
    }

    if (!cache || cache.csib !== csib) {
      disposeCadEditingSession();
      return;
    }

    if (existing) {
      existing.dispose();
    }

    const session = createFeatureExtractionController({
      viewer: viewerWrapper.viewer,
      lineEditingPlugin: viewerWrapper.lineEditingPlugin,
      context: params.featureExtraction,
      logger: params.featureExtractionLogger,
      csib,
      geoDataFiles: cache.geoDataFiles,
      pointCloudRoots: params.pointCloudRoots,
    });
    setCadEditingSession(session);

    onCleanup(() => {
      session.dispose();
      if (untrack(() => cadEditingSession()) === session) {
        setCadEditingSession(undefined);
      }
    });
  });

  onCleanup(() => {
    disposeCadEditingSession();
  });

  const toggleCadEditingMode = (): void => {
    const next = !cadEditingMode();
    if (next) {
      params.onCadEditingEnabled?.();
      setCadEditingMode(true);
    } else {
      cadEditingSession()?.cancelActiveTool();
      setCadEditingMode(false);
      params.onCadEditingDisabled?.();
    }
    params.featureExtractionLogger.info('Toggled CAD editing mode', {
      enabled: next,
    });
  };

  return {
    cadEditingMode,
    cadEditingSession,
    isCadEditingEligible,
    toggleCadEditingMode,
  };
}
